import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  UrlTree,
  Router,
  Params,
  RouterStateSnapshot,
} from '@angular/router';
import { Location } from '@angular/common';
import {
  GetPathwayWithPermissionsParams,
  PathwayFacade,
} from '@dg/pathways-rsm';
import { AuthService } from '@app/shared/services/auth.service';
import { PathwayPermissionsModel } from '@app/pathways/pathway-api.model';
import * as api from '@app/pathways/rsm/pathway-api.model';
import * as helpers from '@app/shared/utils/route-guard-helpers';

@Injectable({
  providedIn: 'root',
})
export class PathwayPageGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private facade: PathwayFacade,
    private location: Location,
    private router: Router
  ) {}

  public canActivate(
    activeSnapshot: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const canActivate = this.hasViewPermissions(activeSnapshot, state.url);
    return canActivate ? canActivate : this.errorHandler();
  }

  private async hasViewPermissions(
    activeSnapshot: ActivatedRouteSnapshot,
    currentPath: string
  ) {
    const queryParams = this.normalizeQueryParams(activeSnapshot.queryParams);
    const { orgsso, tltag, code, editmode } = queryParams;

    let isEditing = editmode;
    let canActivate;

    const isUserLoggedIn = helpers.ensureLoggedIn(
      this.authService,
      this.router,
      activeSnapshot
    );
    if (!isUserLoggedIn) {
      return false;
    }

    // the child route we're trying to navigate to
    let childRoute = activeSnapshot.routeConfig.path;

    const shouldRedirect = this.shouldRedirect(currentPath);
    if (shouldRedirect) {
      // convert the old routes to the corresponding new ones
      if (currentPath.includes('reporting')) {
        childRoute = 'insights/reporting';
      } else if (currentPath.includes('insights')) {
        childRoute = 'insights';
      } else if (currentPath.includes('authoring')) {
        isEditing = true;
        childRoute = 'pathway';
      } else {
        childRoute = 'pathway';
      }
    }

    // if we're on a re-direct route we wont have the obfuscatedId right away..
    let obfuscatedId: string;

    const apiParams = this.getLoadPathwayParam(activeSnapshot, queryParams);
    if (!apiParams) {
      return false;
    }

    // check if the pathway is loaded
    if (!this.facade.snapshot.pathway?.id) {
      await this.facade.loadPathway(apiParams, {
        orgsso,
        tltag,
      });
    }

    // get the obfuscatedId from the internalUrl to help with the redirect
    obfuscatedId = this.facade.snapshot.pathway.internalUrl.split('/').pop();

    canActivate = this.checkPermissions(
      childRoute,
      this.facade.snapshot.permissions,
      isEditing
    );

    if (shouldRedirect && canActivate) {
      // now we should have the obfuscatedId
      const baseRedirect = `/pathway/${obfuscatedId}`;
      const redirectUrl = !!childRoute
        ? `${baseRedirect}/${childRoute}`
        : baseRedirect;

      const redirectTree: UrlTree = this.router.createUrlTree([redirectUrl], {
        queryParams: {
          editMode: isEditing,
          tltag: tltag,
          orgsso: orgsso,
        },
      });

      const serializedUrl = this.router.serializeUrl(redirectTree);

      this.location.replaceState(serializedUrl);
      return this.router.navigateByUrl(serializedUrl);
    }

    return canActivate;
  }

  /**
   * Check if the user has permission to view the route
   */
  private checkPermissions(
    childRoute: string,
    permissions: PathwayPermissionsModel | api.PathwayPermissionsModel,
    isEditMode = false
  ): boolean | UrlTree {
    // We already pre-checked that the user is logged in
    let hasPermissions: boolean = true;
    switch (childRoute) {
      case 'insights':
      case 'insights/reporting':
      case ':obfuscatedId/insights/content':
      case ':obfuscatedId/insights/links':
      case ':obfuscatedId/insights/reporting':
        hasPermissions = permissions.canViewInsightsTab;
        break;
      case 'enrollees':
        hasPermissions = permissions.canViewPathwayEnrollees;
        break;
      case 'pathway':
      case ':obfuscatedId':
      default:
        if (isEditMode) {
          hasPermissions = permissions?.canEditPathway;
        }
    }
    return hasPermissions;
  }

  /**
   * The /api/getpathwaywithuserpermissions needs at least an id to get a pathway
   * Most pathways will have the obfuscatedId, but also catch pathId
   * as a fallback for really old pathway routes. If we cant find either, sends user to 404.
   */
  private getLoadPathwayParam(
    activeSnapshot: ActivatedRouteSnapshot,
    queryParams: Params
  ): GetPathwayWithPermissionsParams {
    // if we're on a regular pathway route, we are *always* on a child to the :obfuscatedId
    // so we get the id from the parent:
    const parentParam = activeSnapshot.parent.params?.obfuscatedId;
    // if we aren't, and routing directly to the insights paths (that are *not* real child routes),
    // then we get the obfuscatedId from the paramMap.
    const paramMap = activeSnapshot.paramMap.get('obfuscatedId');
    const obfuscatedId = paramMap ?? parentParam;

    let param: GetPathwayWithPermissionsParams;
    // check the pathid param *first*, in case we routed from an old route
    if (queryParams?.id) {
      const pathwayId = queryParams?.id;
      param = { pathId: pathwayId };
    } else if (obfuscatedId) {
      param = { obfuscatedId };
    }

    return param;
  }

  /**
   * Lowercase all query params received
   * Safety measure to make sure all query params match have the same casing
   */
  private normalizeQueryParams(params: Params): Params {
    const lowerCaseParams: Params = {};
    for (const key in params) {
      lowerCaseParams[key.toLowerCase()] = params[key];
    }
    return lowerCaseParams;
  }

  /**
   * Determine if the current path includes an old route that should be redirected
   * @param currentPath
   */
  private shouldRedirect(currentPath: string): boolean {
    return currentPath.includes('/paths');
  }

  /**
   * Returns the user to 404
   * @param navigate should we navigate or return url
   */
  private errorHandler() {
    return this.router.navigateByUrl('/error-handler/404');
  }
}
